import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    CheckCircle2,
    ArrowRight,
    FileText,
    Truck,
    Users,
    Route,
    Receipt,
    Download,
    Bookmark
} from 'lucide-react';
import './Solutions.css';
import '../LandingPage.css';

import rapportImg from '../../../assets/rapport.png';
import tmsLogo from '../../../assets/TmsLogoh.png';

const RapportsAnalytics = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const reportTypes = [
        {
            icon: <FileText size={28} color="#4338ca" />,
            title: 'Rapport Général',
            description: 'Une vue d\'ensemble consolidée de toutes vos opérations de transport sur la période sélectionnée.'
        },
        {
            icon: <Truck size={28} color="#4338ca" />,
            title: 'Rapport Véhicules',
            description: 'Analysez l\'utilisation de chaque camion : missions effectuées, charges transportées et temps d\'activité.'
        },
        {
            icon: <Users size={28} color="#4338ca" />,
            title: 'Rapport Chauffeurs',
            description: 'Suivez l\'activité individuelle de chaque conducteur pour évaluer la charge de travail et les performances.'
        },
        {
            icon: <Route size={28} color="#4338ca" />,
            title: 'Rapport Kilomètres',
            description: 'Consultez les distances parcourues par véhicule ou par mission pour maîtriser vos coûts kilométriques.'
        },
        {
            icon: <Receipt size={28} color="#4338ca" />,
            title: 'Rapport Facturation',
            description: 'Récapitulatif des missions facturables par client, pour préparer vos factures sans oubli.'
        },
        {
            icon: <Truck size={28} color="#059669" />,
            title: 'Rapport Sous-traitance',
            description: 'Suivi dédié aux missions confiées à des sous-traitants, avec les volumes et coûts associés.'
        }
    ];

    return (
        <div className="saas-container">
            {/* Navbar */}
            <nav className="landing-nav saas-nav">
                <div className="nav-container">
                    <div className="nav-logo">
                        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
                            <img src={tmsLogo} alt="Bfret Logo" style={{ height: '32px' }} />
                        </Link>
                    </div>
                    <div className="nav-links">
                        <div className="nav-dropdown">
                            <span className="nav-dropdown-toggle">Solutions</span>
                            <div className="nav-dropdown-menu">
                                <Link to="/solutions/planification-intelligente">Planification intelligente</Link>
                                <Link to="/solutions/suivi-temps-reel">Suivi en temps réel</Link>
                                <Link to="/solutions/documents-centralises">Documents centralisés</Link>
                                <Link to="/solutions/rapports-analytics">Rapports & Analytics</Link>
                                <Link to="/solutions/gestion-ressources">Gestion des ressources</Link>
                                <Link to="/solutions/ai-agent-center">AI Agent Center</Link>
                            </div>
                        </div>
                        <Link to="/contact" className="btn-primary saas-btn-primary" style={{ marginLeft: '1rem' }}>Démo</Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section style={{ padding: '6rem 5% 4rem', textAlign: 'center', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <span className="saas-feature-tag" style={{ display: 'inline-block', marginBottom: '1.5rem', backgroundColor: '#e0e7ff', color: '#4338ca', padding: '0.5rem 1rem', borderRadius: '9999px', fontWeight: '600' }}>Rapports & Analytics</span>
                <h1 style={{ fontSize: '3.5rem', maxWidth: '900px', margin: '0 auto 1.5rem', color: '#0f172a', lineHeight: '1.1' }}>
                    Mesurez. Analysez. Décidez.
                </h1>
                <p style={{ fontSize: '1.25rem', maxWidth: '700px', margin: '0 auto 2.5rem', color: '#475569', lineHeight: '1.6' }}>
                    Accédez aux données clés de votre activité transport en quelques clics. Des rapports ciblés par thème, exportables, pour piloter votre exploitation avec les bons chiffres.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <Link to="/contact" className="saas-btn saas-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.1rem', borderRadius: '8px' }}>
                        Demander une démo
                    </Link>
                </div>
            </section>

            {/* Dashboard Image — split layout with intro text */}
            <section className="saas-feature-section" style={{ paddingTop: '5rem', paddingBottom: '4rem', backgroundColor: '#ffffff', borderTop: '1px solid #f1f5f9' }}>
                <div className="saas-feature-grid">
                    <div className="saas-feature-text" style={{ paddingRight: '2rem' }}>
                        <span className="saas-feature-tag">Vue d'ensemble</span>
                        <h2 style={{ fontSize: '2.5rem', lineHeight: '1.2', marginBottom: '1.5rem', color: '#0f172a' }}>
                            Tous vos indicateurs au même endroit
                        </h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', marginBottom: '2rem', lineHeight: '1.6' }}>
                            Le module rapports centralise l'ensemble de vos données d'exploitation dans une interface claire. Sélectionnez votre rapport, appliquez vos filtres et lisez vos chiffres sans effort.
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <CheckCircle2 size={22} color="var(--saas-primary)" />
                                <span style={{ fontSize: '1.05rem', color: '#475569' }}>Filtrage par période sur tous les rapports</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <CheckCircle2 size={22} color="var(--saas-primary)" />
                                <span style={{ fontSize: '1.05rem', color: '#475569' }}>6 rapports thématiques distincts</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <CheckCircle2 size={22} color="var(--saas-primary)" />
                                <span style={{ fontSize: '1.05rem', color: '#475569' }}>Export et vues sauvegardées inclus</span>
                            </div>
                        </div>
                    </div>
                    <div className="saas-feature-visual" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', inset: '-10px', background: 'linear-gradient(to right, #e0e7ff, #f3e8ff)', filter: 'blur(30px)', opacity: '0.5', borderRadius: '20px', zIndex: 0 }}></div>
                            <img
                                src={rapportImg}
                                alt="Interface des rapports BFRET"
                                style={{
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%',
                                    maxWidth: '700px',
                                    borderRadius: '12px',
                                    boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)',
                                    border: '1px solid rgba(0,0,0,0.05)'
                                }}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 6 Report Types Grid */}
            <section style={{ padding: '5rem 5%', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                        <h2 style={{ fontSize: '2.5rem', color: '#0f172a', marginBottom: '1rem' }}>6 types de rapports disponibles</h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', maxWidth: '600px', margin: '0 auto' }}>
                            Chaque rapport est filtrable par période et exportable pour répondre à vos besoins d'analyse ou de reporting client.
                        </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem' }}>
                        {reportTypes.map((report, index) => (
                            <div key={index} style={{
                                padding: '2rem',
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '1rem',
                                transition: 'box-shadow 0.2s ease',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <div style={{ padding: '0.75rem', backgroundColor: '#eef2ff', borderRadius: '10px', flexShrink: 0 }}>
                                        {report.icon}
                                    </div>
                                    <h3 style={{ fontSize: '1.2rem', color: '#0f172a', margin: 0, fontWeight: '600' }}>{report.title}</h3>
                                </div>
                                <p style={{ color: '#475569', margin: 0, lineHeight: '1.6', fontSize: '1rem' }}>
                                    {report.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Export & Saved Views */}
            <section style={{ padding: '5rem 5%', backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))', gap: '2rem' }}>
                    <div style={{ padding: '2.5rem', backgroundColor: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ padding: '0.75rem', backgroundColor: '#e0e7ff', borderRadius: '10px' }}>
                                <Download size={28} color="#4338ca" />
                            </div>
                            <h3 style={{ fontSize: '1.4rem', color: '#0f172a', margin: 0 }}>Export des données</h3>
                        </div>
                        <p style={{ color: '#475569', lineHeight: '1.6', fontSize: '1.05rem', margin: 0 }}>
                            Exportez n'importe quel rapport dans le format de votre choix pour le partager avec votre comptable, vos clients ou l'intégrer dans vos outils internes.
                        </p>
                        <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <CheckCircle2 size={18} color="#4338ca" />
                                <span style={{ color: '#475569', fontSize: '1rem' }}>Export avec filtres personnalisés</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <CheckCircle2 size={18} color="#4338ca" />
                                <span style={{ color: '#475569', fontSize: '1rem' }}>Disponible pour tous les types de rapports</span>
                            </div>
                        </div>
                    </div>

                    <div style={{ padding: '2.5rem', backgroundColor: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ padding: '0.75rem', backgroundColor: '#e0e7ff', borderRadius: '10px' }}>
                                <Bookmark size={28} color="#4338ca" />
                            </div>
                            <h3 style={{ fontSize: '1.4rem', color: '#0f172a', margin: 0 }}>Vues sauvegardées</h3>
                        </div>
                        <p style={{ color: '#475569', lineHeight: '1.6', fontSize: '1.05rem', margin: 0 }}>
                            Configurez votre rapport une fois (filtres, période, type) et sauvegardez cette vue pour la retrouver en un clic lors de vos prochaines analyses.
                        </p>
                        <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <CheckCircle2 size={18} color="#4338ca" />
                                <span style={{ color: '#475569', fontSize: '1rem' }}>Créer, modifier et supprimer vos vues</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <CheckCircle2 size={18} color="#4338ca" />
                                <span style={{ color: '#475569', fontSize: '1rem' }}>Gain de temps sur les rapports récurrents</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Metrics Banner */}
            <section style={{ backgroundColor: '#0f172a', color: 'white', padding: '5rem 5%' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '3rem', textAlign: 'center' }}>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>6</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Types de rapports</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>Export</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Données en un clic</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>100%</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Basé sur vos données réelles</div>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="saas-hero" style={{ padding: '6rem 5%', textAlign: 'center', backgroundColor: '#ffffff' }}>
                <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem', color: '#0f172a' }}>Pilotez votre activité avec des données fiables</h2>
                <p style={{ maxWidth: '600px', margin: '0 auto 2.5rem', fontSize: '1.15rem', color: '#475569', lineHeight: '1.6' }}>
                    Ne prenez plus de décisions à l'aveugle. Accédez à vos rapports d'activité en quelques clics et passez à l'action.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <Link to="/contact" className="saas-btn saas-btn-primary" style={{ padding: '1rem 2.5rem', fontSize: '1.15rem', borderRadius: '8px' }}>
                        Contacter notre équipe <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default RapportsAnalytics;
